import React, { useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation, useHistory, useRouterHelper } from '@shared/core';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { ReactComponent as Guests } from './assets/guests.svg';
import { Search, Add } from '@withjoy/joykit/icons';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';

import { AccommodationPlaceType, AddAccommodationsProps } from './AddAccommodations.types';
import { LayoutList } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutList';
import { LayoutListItemProps } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutList/LayoutList.types';
import { ACCOMMODATIONS_ACTIONS } from '../../Accommodations.constants';
import { usePlanningRoutes } from '@apps/admin/routes/Planning/hooks/usePlanningRoutes';
import { PlanningRoutes } from '@apps/admin/routes/Planning/Planning.routes';
import { CUSTOM_PROPERTY_ADD_KEY } from '../../Accommodations.constants';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { TravelMapBlockType } from '../../Accommodations.types';
import { ADD_STAY_SEARCH_PARAM_KEY } from '@shared/utils/accommodations';
import { CUSTOM_PAGE_ACTIONS } from '../../../CustomComponents.constants';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

export const AddAccommodations: React.FC<AddAccommodationsProps> = props => {
  const { buildPath } = useRouterHelper();
  const { t2 } = useTranslation('websiteEditor');
  const { navigateToScreen, replaceAndNavigateToScreen } = useNavigationPaneContext();
  const { hotelBlockPath } = usePlanningRoutes();
  const history = useHistory();
  const updateSearchParam = useUpdateSearchParam();
  const queryParamHelper = useQueryParamHelper();
  const isAddCustomPropertyOpen = queryParamHelper.getValueString(CUSTOM_PROPERTY_ADD_KEY);
  const telemetry = useAdminAccommodationsTelemetry();

  // Handle auto-navigation to CustomPropertyAdd when coming from BrowseHotels
  useEffect(() => {
    if (isAddCustomPropertyOpen === 'true') {
      navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_ADD);
      // Clear the search param to prevent re-triggering
      updateSearchParam('isCustomAddProperty', null);
    }
  }, [navigateToScreen, updateSearchParam, isAddCustomPropertyOpen]);

  const hotelLabel = t2('customPage', 'travelMap', 'hotel');
  const travelMapAccommodations = t2('customPage', 'itemTypes');

  const handleTelemetryOnItemClick = useCallback((itemType?: TravelMapBlockType, label?: string) => {
    telemetry.addAccommodationItemClicked(props.pageSlug, { itemType, label });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onItemListClicked = useCallback(
    (item: LayoutListItemProps) => {
      if (item.id === AccommodationPlaceType.Hotel) {
        handleTelemetryOnItemClick(TravelMapBlockType.travelMapAccommodationsHotel, 'Add Hotel');

        const params = new URLSearchParams({ [ADD_STAY_SEARCH_PARAM_KEY]: 'true' });
        history.push(buildPath(`${PlanningRoutes.accommodations}/browse?${params.toString()}`));
      } else if (item.id === AccommodationPlaceType.GetRoomBlock) {
        handleTelemetryOnItemClick(TravelMapBlockType.travelMapAccommodationsRoomblock, 'Get Hotel Room Block');
        history.push(hotelBlockPath);
      } else {
        handleTelemetryOnItemClick(TravelMapBlockType.travelMapAccommodationsCustom, 'Other');
        navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_ADD);
      }
    },
    [buildPath, handleTelemetryOnItemClick, history, hotelBlockPath, navigateToScreen],
  );

  const handleClose = useEventCallback(() => {
    replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
  });

  const getLayoutItems = () => {
    return [
      {
        id: uuidv4(),
        title: '',
        children: [
          {
            id: AccommodationPlaceType.Hotel,
            title: travelMapAccommodations.travelMapAccommodationsHotel.listV2.title,
            subtitle: travelMapAccommodations.travelMapAccommodationsHotel.listV2.subtitle,
            icon: Search,
          },
          {
            id: AccommodationPlaceType.GetRoomBlock,
            title: travelMapAccommodations.travelMapAccommodationsGetRoomBlock.listV2.title,
            subtitle: travelMapAccommodations.travelMapAccommodationsGetRoomBlock.listV2.subtitle,
            icon: Guests,
          },
          {
            id: AccommodationPlaceType.Custom,
            title: travelMapAccommodations.travelMapAccommodationsCustom.listV2.title,
            subtitle: travelMapAccommodations.travelMapAccommodationsCustom.listV2.subtitle,
            icon: Add,
          },
        ],
      },
    ];
  };

  return (
    <LayoutList
      title={hotelLabel.addStayCta}
      subTitle={hotelLabel.addStaySubtitle}
      loading={false}
      items={getLayoutItems()}
      handleClose={handleClose}
      onItemClicked={onItemListClicked}
    />
  );
};
